"use client";

import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";
import { FaArrowRight } from "react-icons/fa6";
import { categoryToSlug } from "@/lib/category-slugs";
import { LiveStage } from "../Story/LiveStage";
import { SPOT_TONES, SpotIcon, SpotTile } from "../Spot/SpotIcon";
import { CATEGORIES, CATEGORY_META, type Category } from "./category-meta";

/**
 * Top of the Resources hub: the title with the brand full stop, one line of promise, the
 * way into the getting-started guide, the four categories as tinted cards with their
 * illustrated icon and article count, and the category-deck animation on a live stage
 * (plays when visible, floats while idle, replays on click).
 *
 * Phones get title → animation → guide → categories; from `lg` the animation takes the
 * right column beside the copy.
 *
 * @param locale - Page language.
 * @param counts - Published articles per category, shown on each card.
 */
export function ResourcesIntro({
  locale,
  counts,
}: {
  locale: "fr" | "en";
  counts: Partial<Record<Category, number>>;
}) {
  const intl = useIntl();
  const langSuffix = locale === "en" ? "/en" : "";

  return (
    <section className="mb-14 mt-2 grid gap-7 sm:mb-16 sm:mt-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:gap-x-10 md:gap-y-0 lg:gap-x-16">
      <div className="md:col-start-1 md:row-start-1 md:self-end">
        <h1 className="text-balance text-4xl font-bold leading-[1.05] tracking-[-0.025em] text-title-blue sm:text-5xl 2xl:text-6xl">
          <FormattedMessage id="resources.hero.title" />
          <span className="text-brand">.</span>
        </h1>
        <p className="mt-4 max-w-xl text-lg leading-relaxed text-text-main 2xl:text-xl">
          <FormattedMessage id="resources.hero.lead" />
        </p>
      </div>

      {/* The stage floats while idle; a click replays the deal with a little jiggle. */}
      <LiveStage
        name="resources"
        locale={locale}
        eager
        label={intl.formatMessage({ id: "resources.hero.animation" })}
        replayLabel={intl.formatMessage({ id: "resources.hero.replay" })}
        className="mx-auto aspect-[5/4] max-w-md rounded-[28px] bg-surface/60 shadow-[0_24px_60px_-32px_rgba(97,95,255,0.45)] ring-1 ring-blue-sec/60 hover:shadow-[0_28px_70px_-30px_rgba(97,95,255,0.6)] sm:rounded-[32px] md:col-start-2 md:row-span-2 md:row-start-1 md:mx-0 md:max-w-[460px] md:self-center md:justify-self-end xl:max-w-[520px]"
      />

      <div className="md:col-start-1 md:row-start-2 md:self-start md:pt-7">
        <Link
          href={`/guide${langSuffix}`}
          className="group flex items-center gap-4 rounded-3xl bg-blue-main p-4 pr-5 text-white transition-colors hover:bg-blue-main/90"
        >
          <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-surface p-2 text-brand transition-transform duration-300 group-hover:-rotate-6">
            <SpotIcon name="day" className="h-full w-full" />
          </span>
          <span className="flex-1">
            <span className="block text-base font-semibold">
              <FormattedMessage id="resources.hero.guide.title" />
            </span>
            <span className="mt-0.5 block text-sm text-white/85">
              <FormattedMessage id="resources.hero.guide.desc" />
            </span>
          </span>
          <FaArrowRight aria-hidden="true" className="shrink-0 text-sm transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      {/* The four categories as tinted cards: illustrated icon, name, one line, article count. */}
      <nav aria-labelledby="resources-browse" className="mt-3 md:col-span-2 md:row-start-3 md:mt-14">
        <h2 id="resources-browse" className="text-xl font-bold tracking-[-0.01em] text-title-blue sm:text-2xl">
          <FormattedMessage id="resources.hero.browse" />
        </h2>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {CATEGORIES.map((category) => {
            const meta = CATEGORY_META[category];
            const { text, ground } = SPOT_TONES[meta.tone];
            return (
              <li key={category}>
                <Link
                  href={`/resources/${categoryToSlug(category)}${langSuffix}`}
                  className={`lift-hit group flex h-full items-start gap-4 rounded-3xl p-4 transition-transform duration-300 ease-in-out hover:-translate-y-1 sm:flex-col sm:p-5 ${ground}`}
                >
                  <SpotTile
                    name={meta.spot}
                    tone={meta.tone}
                    onTint
                    className="h-14 w-14 rounded-2xl p-2 transition-transform duration-300 group-hover:-rotate-6 sm:h-16 sm:w-16"
                  />
                  <span className="flex min-w-0 flex-1 flex-col self-stretch">
                    <span className="text-lg font-bold text-title-blue">
                      <FormattedMessage id={meta.labelKey} />
                    </span>
                    <span className="mt-1 text-sm leading-snug text-text-main">
                      <FormattedMessage id={meta.descKey} />
                    </span>
                    <span className={`mt-3 inline-flex items-center gap-1.5 text-sm font-semibold sm:mt-auto sm:pt-4 ${text}`}>
                      <FormattedMessage id="resources.hero.count" values={{ count: counts[category] ?? 0 }} />
                      <FaArrowRight aria-hidden="true" className="text-[10px] transition-transform group-hover:translate-x-1" />
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </section>
  );
}
